import { API_CART_INVENTORY } from '@constants';
import { AuthorizationService } from '@services';
import axios from 'axios';
import { OrderCancellationResponse, OrderExpanded, ShippingMethod } from './interfaces';
import { Order } from '@commercetools/platform-sdk';
import { PaymentProviderFactory } from '@integrations/payment/integrator/factory';
import { CreatePaymentWidgetParams, MountableElement, PaymentMethodsData,
  PaymentProviderIntegration, PaymentResult, PAYMENT_METHODS } from '@integrations/payment';
import { handleServiceError } from '@utils';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';
import { API_HOST_PROXY } from '@constants/api';

export class CheckoutService extends AuthorizationService {
  static readonly serviceName: string = 'CheckoutService';
  private readonly paymentProvider: PaymentProviderIntegration;

  constructor() {
    super();
    const paymentProviderFactory = new PaymentProviderFactory();
    this.paymentProvider = paymentProviderFactory.getPaymentProvider();
  }
  public async getShippingMethods(id: string): Promise<ShippingMethod[] | null> {
    try {
      const url = new URL(`${API_HOST_PROXY}/shipping-methods`);
      url.searchParams.set('cartId', id);
      const { data: shippingMethods } = await axios.get(url.toString());

      return shippingMethods;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async initializePaymentSession(cartId: string, version: number): Promise<PaymentMethodsData | null> {
    return await this.paymentProvider.initiateSession(cartId, version);
  }

  public async makePaymentRequest(
    paymentId: string,
    paymentMethod: PAYMENT_METHODS,
    isSubscription: boolean)
    : Promise<PaymentResult | null> {
    try {
      return this.paymentProvider.makePaymentRequest(paymentId, paymentMethod, isSubscription);
    } catch {
      return null;
    }
  }

  public async placeOrder(cartId: string, cartVersion: number): Promise<OrderExpanded | null> {
    try {
      const { data: order } = await axios.post(`${API_HOST_PROXY}/orders`,
        { version: cartVersion, id: cartId }
      );

      return order;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getMyOrders(storeKey: string): Promise<OrderExpanded[]> {
    try {
      const url = new URL(`${API_HOST_PROXY}/orders/me`);
      if (isFeatureEnabled(FEATURES.MULTI_TENANCY) && storeKey) {
        url.searchParams.set('storeKey', storeKey);
      }
      const { data: orders } = await axios.get(url.toString());

      return orders;
    } catch (err) {
      handleServiceError(err);

      return [];
    }
  }

  public async cancelSubscription(orderId: string, lineItemId: string): Promise<Order | null> {
    try {
      const data = await this.paymentProvider.cancelPayment<Order>(orderId, lineItemId);

      return data;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async cancelOrder(orderId: string): Promise<OrderCancellationResponse> {
    try {
      const { data: { data: { message } } } = await axios.post(`${API_CART_INVENTORY}/cancel-order`, {
        orderId
      });

      return { status: 'confirmed', message };
    } catch (err) {
      handleServiceError(err, { showSnackbar: false });
      const axiosErr = err as { response?: { data?: { errors?: Array<{ message?: string }> } } };

      return { status: 'denied', message: axiosErr?.response?.data?.errors?.[0]?.message ?? 'Unknown Error' };
    }
  }

  public async createPaymentWidget(params: CreatePaymentWidgetParams): Promise<MountableElement> {
    return await this.paymentProvider.createPaymentWidget(params);
  }

  public endPaymentSession(): void {
    this.paymentProvider.endSession();
  }

  public async getStatesWithTaxCategories(): Promise<string[]> {
    try {
      const { data: { states } } = await axios.get(`${API_HOST_PROXY}/tax-categories/states`);

      return states;
    } catch (err) {
      handleServiceError(err);

      return [];
    }
  }
}
